/**
 * CustomerSuccessView — the cockpit that keeps us on top of every live
 * customer.
 *
 * It scores every live customer's health on demand from their real record
 * (see `crm/health.ts`) and ranks them riskiest-first, so the account about to
 * go quiet is the first thing a CS owner sees — not buried in an alphabetical
 * contacts list. Each row explains *why* the account scored the way it did
 * (the transparent factor breakdown) and shows the ARR at stake.
 *
 * The "Run Customer Success agent" button runs the real health producer, which
 * queues a `create_task` check-in proposal for each slipping account into the
 * Dashboard approvals inbox — gated by operator approval, never auto-writing.
 * No fabricated numbers: scores derive only from signals on the record, and
 * the ARR shown is the account's own value (blank when unset).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  HeartPulse,
  ShieldCheck,
  Bot,
  AlertTriangle,
  Activity,
  Info,
  Mail,
  ClipboardList,
  Plus,
  CalendarClock,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { SaveListPanel } from '../components/SaveListPanel';
import { FullAnalysisFold } from '../components/FullAnalysisFold';
import { ArgoSuggestsStrip } from '../components/ArgoSuggestsStrip';
import { ThisWeekPanel } from '../components/ThisWeekPanel';
import { WeeklyDigestPanel } from '../components/WeeklyDigestPanel';
import { WeeklyUpdateReviewModal } from '../components/WeeklyUpdateReviewModal';
import { CustomerWeeklyUpdatePanel } from '../components/CustomerWeeklyUpdatePanel';
import { BatchWeeklyUpdateModal } from '../components/BatchWeeklyUpdateModal';
import { C } from '../lib/tokens';
import { useAuth } from '../lib/auth/AuthProvider';
import { bootstrapPeopleIfEmpty, subscribePeople } from '../lib/db/people';
import { bootstrapTasksIfEmpty, subscribeTasks } from '../lib/db/tasks';
import { bootstrapAccountsIfEmpty, subscribeAccounts } from '../lib/db/accounts';
import { bootstrapTicketsIfEmpty, subscribeTickets } from '../lib/db/tickets';
import {
  subscribeDeliverables,
  saveDeliverable,
  updateDeliverable,
  DELIVERABLE_STATUSES,
} from '../lib/db/deliverables';
import { subscribeRoadmap } from '../lib/db/roadmap';
import { summarizeDeliverables, isDeliverableOverdue } from '../lib/crm/deliverablesSummary';
import { rankCustomersByRisk, type HealthBand } from '../lib/crm/health';
import { assembleWeeklyOps } from '../lib/crm/weeklyOps';
import { proactiveNudges } from '../lib/agent/proactiveNudges';
import { scanSystem } from '../lib/agent/systemScanner';
import { lastTouchAt } from '../lib/crm/score';
import { proposeHealthCheckins } from '../lib/agent/customerHealthAgent';
import { dueTouches, overdueLabel } from '../lib/crm/cadence';
import { proposeCadenceCheckins } from '../lib/agent/cadenceAgent';
import {
  assembleWeeklyUpdate,
  buildBatchWeeklyUpdates,
  resolveWeeklyUpdateRecipient,
  type BatchWeeklyUpdateEntry,
  type WeeklyUpdateDigest,
  type WeeklyUpdateRecipient,
} from '../lib/crm/weeklyUpdate';
import {
  computeChurnRisk,
  deriveChurnInput,
  toOutcomeContract,
  verifyPredictionPrecision,
  type ChurnRiskResult,
} from '../lib/crm/churnRisk';
import { ChurnRiskCard } from '../lib/crm/churnRiskCard';
import {
  churnTier,
  deriveSignals,
  explainableHealth,
  trend,
  type ChurnTierResult,
  type ExplainedHealth,
  type TrendResult,
} from '../lib/crm/healthExplain';
import {
  biggestNegativeFactor,
  filterByBand,
  summarizeBook,
  weeklyUpdatesDueCount,
} from '../lib/crm/healthSummary';
import { resolveCustomerUpdateRecipient } from '../lib/cs/weeklyUpdate';
import { rankSaveList } from '../lib/cs/savePlays';
import { SavePlaybookPanel } from '../lib/crm/savePlaybookPanel';
import type { SaveAccount } from '../lib/crm/savePlaybook';
import { SavePlayTriagePanel, type SaveTriageRow } from '../lib/crm/savePlayTriagePanel';
import { subscribeHealthHistory } from '../lib/db/healthHistory';
import { isConfigured as agentWorkerConfigured, runAgent } from '../lib/agent/argoWorker';
import type {
  Account,
  CustomerHealthSnapshot,
  Deliverable,
  Person,
  ProjectTask,
  RoadmapItem,
  Ticket,
} from '../lib/db/types';

/** Per-band presentation: dot color, pill class, and label. */
const BAND_META: Record<HealthBand, { label: string; dot: string; pill: string }> = {
  'at-risk': { label: 'At risk', dot: C.magenta, pill: 'glass-pill glass-pill--magenta' },
  watch: { label: 'Watch', dot: C.orange, pill: 'glass-pill glass-pill--orange' },
  healthy: { label: 'Healthy', dot: C.green, pill: 'glass-pill glass-pill--blue' },
};

/** Per-status presentation for the first-class deliverables (Ask #1). */
const DELIVERABLE_STATUS_META: Record<Deliverable['status'], { label: string; pill: string }> = {
  owed: { label: 'Owed', pill: 'glass-pill glass-pill--orange' },
  'in-progress': { label: 'In progress', pill: 'glass-pill glass-pill--blue' },
  delivered: { label: 'Delivered', pill: 'glass-pill glass-pill--green' },
  accepted: { label: 'Accepted', pill: 'glass-pill glass-pill--green' },
};

function usd(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

/**
 * Data-sufficiency check: a brand-new account with no billing history and no
 * logged touch has too few signals for the score to mean much. We still show
 * the number (it's what the rules produced) but mark it provisional rather
 * than implying precision the record can't back up.
 */
function isLowSignal(person: Person): boolean {
  const hasBilling = (person.billingDates ?? []).length > 0;
  const hasTouch = lastTouchAt(person) !== undefined;
  return !hasBilling && !hasTouch;
}

/** '−40 · Past-due billing' — signed points first so the math is visible. */
function factorText(points: number, label: string): string {
  const sign = points < 0 ? '−' : '+';
  return `${sign}${Math.abs(points)} · ${label}`;
}

/** Churn-tier chip class — low is calm, med warns, high/critical alarm. */
const TIER_PILL: Record<ChurnTierResult['tier'], string> = {
  low: 'glass-pill glass-pill--blue',
  med: 'glass-pill glass-pill--orange',
  high: 'glass-pill glass-pill--magenta',
  critical: 'glass-pill glass-pill--magenta',
};

/** Explainable-band accent colors (segment-judged red/yellow/green). */
const EXPLAIN_BAND_COLOR: Record<ExplainedHealth['band'], string> = {
  red: C.magenta,
  yellow: C.orange,
  green: C.green,
};

/** Tiny inline health sparkline from the persisted weekly history. */
function HealthSparkline({ points, color }: { points: number[]; color: string }) {
  const w = 56;
  const h = 16;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = Math.max(1, max - min);
  const step = points.length > 1 ? w / (points.length - 1) : 0;
  const d = points
    .map(
      (p, i) =>
        `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(h - 1 - ((p - min) / span) * (h - 2)).toFixed(1)}`,
    )
    .join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" style={{ display: 'block', flex: '0 0 auto' }}>
      <path d={d} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Circular health dial — the score in the middle, ring filled to score/100
 * and colored by tier. A `null` score renders an honest em-dash (empty book),
 * and `provisional` renders `~score` at reduced opacity (low-signal account).
 */
function HealthDial({
  score,
  color,
  size,
  label,
  provisional = false,
}: {
  score: number | null;
  color: string;
  size: number;
  label: string;
  provisional?: boolean;
}) {
  const stroke = Math.max(3, Math.round(size / 12));
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const frac = score === null ? 0 : Math.max(0, Math.min(100, score)) / 100;
  return (
    <svg
      role="img"
      aria-label={label}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ flex: '0 0 auto', display: 'block' }}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="rgba(255,255,255,0.12)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={`${(frac * circ).toFixed(1)} ${circ.toFixed(1)}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        opacity={provisional ? 0.65 : 1}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        fill={color}
        fontSize={size * 0.3}
        fontWeight={700}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {score === null ? '—' : provisional ? `~${score}` : score}
      </text>
    </svg>
  );
}

export function CustomerSuccessView() {
  const { hasRole } = useAuth();
  const canMutate = hasRole('staff');

  const [people, setPeople] = useState<Person[]>([]);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [roadmap, setRoadmap] = useState<RoadmapItem[]>([]);
  const [healthHistory, setHealthHistory] = useState<CustomerHealthSnapshot[]>([]);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<string | null>(null);
  const [cadenceRunning, setCadenceRunning] = useState(false);
  const [cadenceResult, setCadenceResult] = useState<string | null>(null);
  // The weekly-update draft under review (null = no modal open). Assembled
  // from the live boards at click time; the modal is the approval gate.
  const [draft, setDraft] = useState<{
    account: Account;
    digest: WeeklyUpdateDigest;
    recipient: WeeklyUpdateRecipient | null;
  } | null>(null);
  // The batch under review (null = no batch modal open). Assembled from the
  // live boards at click time; the modal is the approval gate for ALL sends.
  const [batch, setBatch] = useState<BatchWeeklyUpdateEntry[] | null>(null);

  useEffect(() => {
    let active = true;
    void bootstrapPeopleIfEmpty();
    void bootstrapTasksIfEmpty();
    void bootstrapAccountsIfEmpty();
    void bootstrapTicketsIfEmpty();
    const unsubs = [
      subscribePeople((r) => active && setPeople(r)),
      subscribeTasks((r) => active && setTasks(r)),
      subscribeAccounts((r) => active && setAccounts(r)),
      subscribeTickets((r) => active && setTickets(r)),
      subscribeDeliverables((r) => active && setDeliverables(r)),
      subscribeRoadmap((r) => active && setRoadmap(r)),
      subscribeHealthHistory((r) => active && setHealthHistory(r)),
    ];
    return () => {
      active = false;
      unsubs.forEach((u) => u());
    };
  }, []);

  // Ranked riskiest-first. Recomputed on demand so it can never drift.
  const ranked = useMemo(() => rankCustomersByRisk(people, { tasks }), [people, tasks]);

  // Explainable / segmented / predictive layer (auditor fix): per-account
  // signed drivers, segment-judged band, weekly trend from the persisted
  // history already loaded above, and a named-indicator churn tier — all
  // pure math over the live record, recomputed so it can never drift.
  const explainedById = useMemo(() => {
    const map = new Map<
      string,
      { explained: ExplainedHealth; churn: ChurnTierResult; trendResult: TrendResult }
    >();
    for (const { person } of ranked) {
      const history = healthHistory.filter((s) => s.personId === person.id);
      const explained = explainableHealth(person, deriveSignals(person, { tasks }));
      map.set(person.id, {
        explained,
        churn: churnTier(explained, history),
        trendResult: trend(history, 6),
      });
    }
    return map;
  }, [ranked, tasks, healthHistory]);

  // Cadence engine (Ask #1): accounts whose silence exceeds their per-tier
  // touch cadence, most-overdue first. Pure math over the live snapshots.
  const overdueTouches = useMemo(() => dueTouches(accounts, people, Date.now()), [accounts, people]);

  // The 3-second header roll-up: average health, tier counts, and ARR at
  // risk over the ALREADY-scored book (tested pure helper — no re-scoring).
  const bookSummary = useMemo(
    () =>
      summarizeBook(
        ranked.map(({ person, health }) => ({
          health,
          ...(person.accountValue != null ? { arr: person.accountValue } : {}),
        })),
      ),
    [ranked],
  );

  // Weekly updates that can actually send this week — accounts with a
  // resolvable recipient (same canonical resolver as the send pipeline).
  const updatesDue = useMemo(() => weeklyUpdatesDueCount(accounts, people), [accounts, people]);

  // Tier filter driven by the tappable header chips. null = the whole book.
  const [bandFilter, setBandFilter] = useState<HealthBand | null>(null);
  // Riskiest-first stays the default order (rankCustomersByRisk already
  // sorts ascending by score); the chips only narrow, never reorder.
  const visibleRanked = useMemo(() => filterByBand(ranked, bandFilter), [ranked, bandFilter]);

  // Accounts alphabetically — the weekly-update list is a roster, not a ranking.
  const sortedAccounts = useMemo(
    () => [...accounts].sort((a, b) => a.name.localeCompare(b.name)),
    [accounts],
  );

  // First-class deliverables grouped per account for the Ask #1 panel.
  const deliverablesByAccount = useMemo(() => {
    const map = new Map<string, Deliverable[]>();
    for (const d of deliverables) {
      const list = map.get(d.accountId);
      if (list) list.push(d);
      else map.set(d.accountId, [d]);
    }
    return map;
  }, [deliverables]);

  const deliverablesSummary = useMemo(() => summarizeDeliverables(deliverables), [deliverables]);

  // Explainable churn-risk fusion (auditor fix): every ranked customer's
  // seven-dimension risk, derived ONLY from their real record + persisted
  // health history — recomputed from live snapshots so it can never drift.
  const churnResults = useMemo<ChurnRiskResult[]>(() => {
    const accountIdByName = new Map(accounts.map((a) => [a.name.trim().toLowerCase(), a.id]));
    return ranked
      .map(({ person }) => {
        const accountId = accountIdByName.get((person.company ?? '').trim().toLowerCase());
        const input = deriveChurnInput(person, {
          tasks,
          deliverables: accountId ? deliverables.filter((d) => d.accountId === accountId) : [],
          history: healthHistory.filter((s) => s.personId === person.id),
        });
        return computeChurnRisk(input);
      })
      .sort((a, b) => b.churnProbability - a.churnProbability);
  }, [ranked, accounts, tasks, deliverables, healthHistory]);

  // Precision over the churn book: contracts are open until reality closes
  // them, so the header reports honestly ("N open") instead of inventing a
  // hit-rate. Feeds verifyPredictionPrecision the sealed contracts as-is.
  const churnPrecision = useMemo(
    () =>
      verifyPredictionPrecision(
        churnResults.map((r) => toOutcomeContract(r.accountId, r)),
        [],
      ),
    [churnResults],
  );

  // Churn-save playbook enrollment pool: the same live customers the churn
  // engine scored, reduced to the structural shape the trigger engine reads.
  // Real ids/names/ARR only — an account with no value enrolls at $0 risk.
  const saveAccounts = useMemo<SaveAccount[]>(
    () =>
      ranked.map(({ person }) => ({
        id: person.id,
        name: person.name,
        ...(person.accountValue != null ? { accountValue: person.accountValue } : {}),
      })),
    [ranked],
  );

  // Save-play triage rows: every ranked customer's explained health + churn
  // tier plus the PREVIOUS persisted score (the threshold-crossing proof) —
  // pure projections of the live snapshots, nothing fabricated. The CSM of
  // record is the person's real ownerName; ARR is the account's own value.
  const saveTriageRows = useMemo<SaveTriageRow[]>(() => {
    const rows: SaveTriageRow[] = [];
    for (const { person } of ranked) {
      const ex = explainedById.get(person.id);
      if (!ex) continue;
      const history = healthHistory
        .filter((s) => s.personId === person.id)
        .sort((a, b) => a.date.localeCompare(b.date));
      const prev = history.length >= 2 ? history[history.length - 2] : undefined;
      rows.push({
        account: {
          id: person.id,
          name: person.name,
          ...(person.accountValue != null ? { arr: person.accountValue } : {}),
          ...(person.ownerName ? { csmOwner: person.ownerName } : {}),
        },
        explained: ex.explained,
        churn: ex.churn,
        ...(prev ? { prevScore: prev.score } : {}),
      });
    }
    return rows;
  }, [ranked, explainedById, healthHistory]);

  // THE INVERSION: the ranked "Who do I save today?" plan — at most five
  // accounts by (churn probability × ARR at stake), each with the ONE
  // deterministic play chosen from the churn engine's own top contributor.
  // Pure projection of the already-computed health + churn + scan findings;
  // nothing is re-scored, nothing is invented.
  const scanFindings = useMemo(
    () => scanSystem({ tickets, tasks, people, accounts }).findings,
    [tickets, tasks, people, accounts],
  );
  const saveList = useMemo(
    () => rankSaveList(ranked, churnResults, scanFindings),
    [ranked, churnResults, scanFindings],
  );

  /**
   * Agentic narrate seam: route through the real Argo worker when it is
   * configured; any failure (or no worker) resolves to the deterministic
   * narrative already on the result — the card labels the source honestly.
   */
  async function narrateChurn(result: ChurnRiskResult): Promise<string> {
    if (!agentWorkerConfigured()) return result.narrative;
    try {
      const { text } = await runAgent(
        {
          id: 'churn-narrator',
          name: 'Churn Narrator',
          template: 'churn-risk',
          persona: 'Concise CS risk analyst',
        },
        { result },
      );
      return text.trim() || result.narrative;
    } catch {
      return result.narrative;
    }
  }

  // Autopilot "This Week" (bold bet #2): the whole week's ops across ALL
  // accounts, pre-assembled into ONE ranked, reasoned queue — recomputed
  // from the live snapshots so it can never drift.
  const weeklyOps = useMemo(() => {
    const { findings } = scanSystem({ tickets, tasks, people, accounts });
    return assembleWeeklyOps({ people, accounts, tasks, tickets, deliverables, findings });
  }, [people, accounts, tasks, tickets, deliverables]);

  // Proactive nudges (same tested engine as the Copilot dock) — the agent's
  // top suggested moves for this book, each a governed proposal, recomputed
  // from the live snapshots so it can never drift.
  const nudges = useMemo(() => {
    const { findings } = scanSystem({ tickets, tasks, people, accounts });
    return proactiveNudges({ people, tasks, findings });
  }, [people, tasks, tickets, accounts]);

  /**
   * Bold bet #1: assemble a digest for EVERY account from the LIVE boards
   * and open the batch review gate — one action, still human-approved.
   */
  function onDraftAllWeeklyUpdates() {
    setBatch(buildBatchWeeklyUpdates(accounts, { tasks, tickets, people }));
  }

  /** Assemble the digest from the LIVE boards and open the review gate. */
  function onDraftWeeklyUpdate(account: Account) {
    setDraft({
      account,
      digest: assembleWeeklyUpdate(account, { tasks, tickets }),
      recipient: resolveWeeklyUpdateRecipient(account, people),
    });
  }

  async function onRunAgent() {
    setRunning(true);
    setRunResult(null);
    try {
      const { proposed, skipped } = await proposeHealthCheckins(people, tasks);
      if (proposed === 0 && skipped === 0) {
        setRunResult('Every live account is healthy — no check-ins needed');
      } else {
        const bits: string[] = [];
        if (proposed > 0) bits.push(`Proposed ${proposed} check-in${proposed === 1 ? '' : 's'}`);
        if (skipped > 0) bits.push(`${skipped} already awaiting approval`);
        setRunResult(`${bits.join(' · ')} — review in the Dashboard inbox`);
      }
    } catch {
      setRunResult('Run failed — try again');
    } finally {
      setRunning(false);
    }
  }

  /**
   * Governed cadence run (Ask #1): queue a `create_task` check-in proposal for
   * every overdue-touch account via the same approvals path as onRunAgent —
   * landing in the Dashboard inbox + Decision Ledger, never auto-writing.
   */
  async function onProposeCheckins() {
    setCadenceRunning(true);
    setCadenceResult(null);
    try {
      const { proposed, skipped } = await proposeCadenceCheckins(accounts, people);
      if (proposed === 0 && skipped === 0) {
        setCadenceResult('All accounts current — nothing to propose');
      } else {
        const bits: string[] = [];
        if (proposed > 0) bits.push(`Proposed ${proposed} check-in${proposed === 1 ? '' : 's'}`);
        if (skipped > 0) bits.push(`${skipped} already awaiting approval`);
        setCadenceResult(`${bits.join(' · ')} — review in the Dashboard inbox`);
      }
    } catch {
      setCadenceResult('Run failed — try again');
    } finally {
      setCadenceRunning(false);
    }
  }

  return (
    <div className="app-container" style={{ display: 'grid', gap: 16 }}>
      <div>
        <span className="t-eyebrow">Customer success</span>
        <h1 className="t-page-title" style={{ margin: '6px 0 0' }}>
          Stay on top of every live customer
        </h1>
        <p className="t-meta" style={{ margin: '6px 0 0' }}>
          Every live account scored on demand from its real record — renewal proximity, past-due
          billing, touch recency, blocked deliverables, and overdue tasks — ranked riskiest first.
        </p>
      </div>

      {/* 3-second header band: big average-health dial, tappable tier filter
          chips, ARR at risk, and weekly updates due — every number from the
          already-scored book (summarizeBook), nothing recomputed or invented. */}
      <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
        {bookSummary.total === 0 ? (
          <div className="crm-empty" style={{ padding: '12px 0' }}>
            <HeartPulse size={20} strokeWidth={1.75} />
            <span>No live customers yet — book health appears once a prospect converts.</span>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            <HealthDial
              score={bookSummary.avgScore}
              color={
                bookSummary.counts['at-risk'] > 0
                  ? C.magenta
                  : bookSummary.counts.watch > 0
                    ? C.orange
                    : C.green
              }
              size={72}
              label={`Average health ${bookSummary.avgScore ?? '—'} of 100 across ${bookSummary.total} live customer${bookSummary.total === 1 ? '' : 's'}`}
            />
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <span className="t-eyebrow">Book health</span>
              <div className="t-meta" style={{ marginTop: 4 }}>
                Average of {bookSummary.total} live customer
                {bookSummary.total === 1 ? '' : 's'} — tap a tier to filter the list below
              </div>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}
              >
                {(['at-risk', 'watch', 'healthy'] as HealthBand[]).map((band) => (
                  <button
                    key={band}
                    type="button"
                    className={BAND_META[band].pill}
                    aria-pressed={bandFilter === band}
                    onClick={() => setBandFilter((cur) => (cur === band ? null : band))}
                    title={`Show only ${BAND_META[band].label.toLowerCase()} customers (tap again to clear)`}
                    style={{
                      cursor: 'pointer',
                      opacity: bandFilter === null || bandFilter === band ? 1 : 0.45,
                      boxShadow: bandFilter === band ? `0 0 0 1.5px ${BAND_META[band].dot}` : undefined,
                    }}
                  >
                    {bookSummary.counts[band]} {BAND_META[band].label.toLowerCase()}
                  </button>
                ))}
                {bandFilter !== null && (
                  <button
                    type="button"
                    className="glass-btn"
                    onClick={() => setBandFilter(null)}
                    style={{ fontSize: 11 }}
                  >
                    Clear filter
                  </button>
                )}
              </div>
            </div>
            <div style={{ flex: '0 0 auto' }}>
              <span className="t-eyebrow">ARR at risk</span>
              <div
                className="t-card-title"
                style={{
                  marginTop: 4,
                  fontVariantNumeric: 'tabular-nums',
                  ...(bookSummary.arrAtRisk > 0 ? { color: C.magenta } : {}),
                }}
                title="Sum of account value across at-risk customers — accounts with no value on record add nothing"
              >
                {usd(bookSummary.arrAtRisk)}
              </div>
              <div className="t-meta" style={{ marginTop: 2 }}>
                across {bookSummary.counts['at-risk']} at-risk account
                {bookSummary.counts['at-risk'] === 1 ? '' : 's'}
              </div>
            </div>
            <div style={{ flex: '0 0 auto' }}>
              <span className="t-eyebrow">Weekly updates due</span>
              <div
                className="t-card-title"
                style={{ marginTop: 4, fontVariantNumeric: 'tabular-nums' }}
                title="Accounts with a resolvable recipient email — each drafts in the Weekly customer updates panel below"
              >
                {updatesDue}
              </div>
              <div className="t-meta" style={{ marginTop: 2 }}>
                of {accounts.length} account{accounts.length === 1 ? '' : 's'} can send
              </div>
            </div>
          </div>
        )}
      </GlassPanel>

      {/* THE INVERSION — the Save-list renders FIRST: "Who do I save today?"
          answered in 3 seconds. At most 5 ranked cards, each with ONE
          recommended play, the drafted artifact behind [Run save play]
          (approval-gated — never a direct send), and [Why?] folding open
          that account's churn dimensions + findings. */}
      <SaveListPanel
        entries={saveList}
        totalCustomers={ranked.length}
        canMutate={canMutate}
        deliverables={deliverables}
      />

      {/* Progressive disclosure: EVERYTHING the plan was derived from — the
          nudge strip, weekly ops queue, churn explainability tables, save
          playbooks, health rows, deliverables, digests, and weekly updates —
          stays fully reachable behind this one fold. Nothing was removed,
          only tucked below the plan. */}
      <FullAnalysisFold label="Show full analysis — churn tables · findings · deliverables · weekly updates">

      {/* Proactive "Argo suggests" strip — the top nudges from the tested
          nudge engine (at-risk check-ins, overdue-invoice reminders, renewal
          prep). Accepting one queues the governed action into the Dashboard
          approvals inbox; renders nothing when the book is clean. */}
      <ArgoSuggestsStrip nudges={nudges} canMutate={canMutate} />

      {/* Autopilot "This Week" — the ranked cross-account ops queue. */}
      <ThisWeekPanel items={weeklyOps} />

      {/* Explainable churn-risk fusion — replaces the opaque number with
          per-dimension sparklines, Δ badges, causal drivers, and the
          falsifiable contract + precision header. */}
      <ChurnRiskCard
        results={churnResults}
        precision={churnPrecision}
        loading={people.length === 0 && healthHistory.length === 0}
        narrate={narrateChurn}
      />

      {/* Agentic churn-save playbook: trigger enrollment over the SAME churn
          results, blast-radius-simulated batch runs with carve-outs, triaged
          one-gesture approval, earned per-playbook autonomy, and closed-loop
          effectiveness. The run button queues governed proposals into the
          Dashboard approvals inbox — never a hidden write. */}
      <SavePlaybookPanel
        accounts={saveAccounts}
        riskResults={churnResults}
        canRun={canMutate}
        loading={people.length === 0 && healthHistory.length === 0}
      />

      {/* Risk / Save-Plays triage inbox: churn/health signals fire only on
          threshold crossings + named indicators, each spawning a reversible,
          blast-radius-simulated, outcome-contracted play. Launching queues
          ONE batched approval into the Dashboard inbox; Rollback is a single
          atomic unwind; outcome badges are verifier-backed saved-ARR. */}
      <SavePlayTriagePanel
        rows={saveTriageRows}
        healthHistory={healthHistory}
        canRun={canMutate}
        loading={people.length === 0 && healthHistory.length === 0}
      />

      <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
        <div className="dash-section-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              aria-hidden="true"
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                display: 'inline-grid',
                placeItems: 'center',
                background: bookSummary.counts['at-risk']
                  ? 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.12))'
                  : 'linear-gradient(135deg, rgba(0,200,120,0.20), rgba(0,130,243,0.10))',
                border: '1px solid rgba(255,255,255,0.14)',
                color: bookSummary.counts['at-risk'] ? C.magenta : C.green,
              }}
            >
              <HeartPulse size={17} strokeWidth={2} />
            </span>
            <div>
              <span className="t-eyebrow">Customer success</span>
              <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
                Customer health
              </h2>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {canMutate && (
              <button
                className="glass-btn"
                onClick={() => void onRunAgent()}
                disabled={running}
                title="Scores every live customer and queues a check-in proposal for each slipping account into the Dashboard approvals inbox. Approve there to create the task."
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                <Bot size={13} /> {running ? 'Scoring…' : 'Run Customer Success agent'}
              </button>
            )}
            {/* Tier counts live in the header band above (tappable chips);
                here we only surface the active filter, honestly. */}
            {bandFilter !== null && (
              <span className={BAND_META[bandFilter].pill}>
                Filtered: {visibleRanked.length} of {ranked.length} · {BAND_META[bandFilter].label.toLowerCase()}
              </span>
            )}
          </div>
        </div>

        <div
          className="t-meta"
          style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 10px' }}
        >
          <Info size={12} aria-hidden="true" />
          <span>
            Heuristic score from your live record — deterministic rules, not an outcome-validated
            model. Every point is itemized below.
          </span>
        </div>

        {runResult && (
          <div
            className="t-meta"
            style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 10px' }}
          >
            <Activity size={12} color={C.magentaSoft} /> {runResult}
          </div>
        )}

        {ranked.length === 0 ? (
          <div className="crm-empty" style={{ padding: '20px 0' }}>
            <ShieldCheck size={20} strokeWidth={1.75} />
            <span>No live customers yet — convert a prospect to start tracking health.</span>
          </div>
        ) : visibleRanked.length === 0 ? (
          <div className="crm-empty" style={{ padding: '20px 0' }}>
            <ShieldCheck size={20} strokeWidth={1.75} />
            <span>
              No {bandFilter ? BAND_META[bandFilter].label.toLowerCase() : ''} customers right now —
              clear the tier chip above to see the whole book.
            </span>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: '4px 0 0', padding: 0, display: 'grid', gap: 8 }}>
            {visibleRanked.map(({ person, health }) => {
              const meta = BAND_META[health.band];
              const lowSignal = isLowSignal(person);
              // Every non-zero factor, heaviest debits first — the exact
              // itemized reasons the score is what it is (no recomputation).
              const drivingFactors = [...health.factors]
                .filter((f) => f.points !== 0)
                .sort((a, b) => a.points - b.points);
              // Explainable layer for this account (pure, precomputed above).
              const explainRow = explainedById.get(person.id);
              // ONE-line "biggest factor": the heaviest debit among the
              // signed drivers the explainability layer ALREADY computed
              // (the same numbers the churn tier reads); falls back to the
              // score's own itemized factors when the layer has no reading.
              const biggest = biggestNegativeFactor(
                explainRow && explainRow.explained.drivers.length > 0
                  ? explainRow.explained.drivers.map((d) => ({ label: d.label, points: d.deltaPts }))
                  : health.factors,
              );
              // Weekly-update readiness chip — the same canonical resolver
              // the send pipeline uses; "needs email" is an honest state.
              const updateRecipient = resolveCustomerUpdateRecipient(person);
              return (
                <li
                  key={person.id}
                  className="glass-row"
                  data-testid="cs-health-row"
                  style={{ display: 'grid', gap: 8, padding: '10px 12px' }}
                >
                  {/* Lead line — the 1-glance read: dial · name · biggest
                      factor · ARR · weekly-update chip · tier pill. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <HealthDial
                      score={health.score}
                      color={meta.dot}
                      size={38}
                      provisional={lowSignal}
                      label={
                        lowSignal
                          ? `Health ~${health.score} of 100 — provisional: no billing or touch history yet`
                          : `Health ${health.score} of 100 — ${meta.label}`
                      }
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span
                        className="t-card-title"
                        style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {person.name}
                        {person.company ? <span className="t-meta"> · {person.company}</span> : null}
                      </span>
                      <span
                        className="t-meta"
                        data-testid="cs-biggest-factor"
                        title="The single heaviest negative contributor from the explainability layer — the first thing to fix"
                        style={{ display: 'block', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {biggest
                          ? `Biggest factor: ${biggest.label} (−${Math.abs(biggest.points)} pts)`
                          : 'No negative drivers — nothing dragging this account'}
                      </span>
                    </span>
                    {person.accountValue != null && (
                      <span
                        className="t-meta"
                        title="Annual account value at stake"
                        style={{ flex: '0 0 auto', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {usd(person.accountValue)} ARR
                      </span>
                    )}
                    <span
                      className={updateRecipient ? 'glass-pill glass-pill--blue' : 'glass-pill'}
                      style={{ flex: '0 0 auto', fontSize: 10 }}
                      title={
                        updateRecipient
                          ? `Weekly update sends to ${updateRecipient.email} — draft it under Explain below`
                          : 'No email on file — the weekly update cannot send until one is added'
                      }
                    >
                      <Mail size={10} style={{ marginRight: 3 }} />
                      {updateRecipient ? 'Update ready' : 'Update needs email'}
                    </span>
                    <span className={meta.pill} style={{ flex: '0 0 auto' }}>
                      {health.band === 'at-risk' && <AlertTriangle size={11} style={{ marginRight: 3 }} />}
                      {meta.label}
                    </span>
                  </div>

                  {/* Secondary detail — present but not shouting: the full
                      itemized factors, explainable drivers + trend + churn
                      tier, and the governed weekly-update panel. */}
                  <details>
                    <summary
                      className="glass-pill"
                      style={{ cursor: 'pointer', listStyle: 'none', fontSize: 10, width: 'fit-content' }}
                      title="Expand the full breakdown: itemized factors, explainable drivers, trend, churn tier, and the weekly-update panel"
                    >
                      Explain — factors · trend · churn · weekly update
                    </summary>
                    <span style={{ display: 'block', marginTop: 8 }}>
                    <span
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 4,
                        marginTop: 4,
                      }}
                    >
                      {lowSignal && (
                        <span
                          className="glass-pill"
                          title="Too few signals to score confidently — no billing history and no logged touch yet. Treat the number as provisional."
                          style={{ fontSize: 10, opacity: 0.85 }}
                        >
                          Provisional · low signal
                        </span>
                      )}
                      {drivingFactors.length === 0 ? (
                        <span className="t-meta">No risk signals — healthy</span>
                      ) : (
                        drivingFactors.map((f) => (
                          <span
                            key={f.label}
                            className="glass-pill"
                            title={`This factor moved the score by ${f.points} points`}
                            style={{ fontSize: 10 }}
                          >
                            {factorText(f.points, f.label)}
                          </span>
                        ))
                      )}
                    </span>
                    {explainRow && (
                      <span
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 6,
                          marginTop: 6,
                        }}
                      >
                        {/* Driver breakdown popover — every signed driver row. */}
                        <details style={{ position: 'relative' }}>
                          <summary
                            className="glass-pill"
                            style={{ cursor: 'pointer', listStyle: 'none', fontSize: 10 }}
                            title={`Explainable health: base 50 plus signed drivers under ${explainRow.explained.segment}-segment weights, judged by that segment's own band thresholds. Click for the per-factor breakdown.`}
                          >
                            Why · {explainRow.explained.segment} · {explainRow.explained.score}
                          </summary>
                          <span
                            style={{
                              position: 'absolute',
                              zIndex: 20,
                              top: 'calc(100% + 6px)',
                              left: 0,
                              minWidth: 250,
                              padding: '10px 12px',
                              borderRadius: 12,
                              background: 'rgba(10,14,24,0.96)',
                              border: '1px solid rgba(255,255,255,0.14)',
                              boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
                              display: 'grid',
                              gap: 5,
                            }}
                          >
                            <span className="t-meta" style={{ fontSize: 10 }}>
                              Base 50 ± signed drivers · {explainRow.explained.segment} weights ·
                              band {explainRow.explained.band}
                            </span>
                            {explainRow.explained.drivers.map((d) => (
                              <span
                                key={d.signal}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  gap: 10,
                                  fontSize: 11,
                                }}
                              >
                                <span
                                  className="t-meta"
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                >
                                  {d.direction === 'down' ? (
                                    <TrendingDown size={10} color={C.magenta} aria-hidden="true" />
                                  ) : (
                                    <TrendingUp size={10} color={C.green} aria-hidden="true" />
                                  )}
                                  {d.label}
                                </span>
                                <span
                                  style={{
                                    fontVariantNumeric: 'tabular-nums',
                                    color: d.deltaPts < 0 ? C.magenta : C.green,
                                  }}
                                  title={`Weight ${d.weight} · WoW ${d.direction}`}
                                >
                                  {d.deltaPts < 0 ? '−' : '+'}
                                  {Math.abs(d.deltaPts).toFixed(1)} pts
                                </span>
                              </span>
                            ))}
                          </span>
                        </details>
                        {/* Health sparkline + delta badge from persisted history. */}
                        {explainRow.trendResult.sparkline.length >= 2 ? (
                          <>
                            <HealthSparkline
                              points={explainRow.trendResult.sparkline}
                              color={EXPLAIN_BAND_COLOR[explainRow.explained.band]}
                            />
                            <span
                              className="glass-pill"
                              style={{ fontSize: 10 }}
                              title={
                                explainRow.trendResult.crossedThreshold
                                  ? `Crossed ${explainRow.trendResult.crossedThreshold.from} → ${explainRow.trendResult.crossedThreshold.to} inside this window`
                                  : 'Health movement across the charted weeks'
                              }
                            >
                              {explainRow.trendResult.deltaPts < 0 ? '−' : '+'}
                              {Math.abs(explainRow.trendResult.deltaPts)} in{' '}
                              {(explainRow.trendResult.sparkline.length - 1) * 7}d
                            </span>
                          </>
                        ) : (
                          <span className="t-meta" style={{ fontSize: 10 }}>
                            Trend pending — needs 2+ weekly snapshots
                          </span>
                        )}
                        {explainRow.trendResult.crossedThreshold && (
                          <span className="glass-pill glass-pill--orange" style={{ fontSize: 10 }}>
                            {explainRow.trendResult.crossedThreshold.from} →{' '}
                            {explainRow.trendResult.crossedThreshold.to}
                          </span>
                        )}
                        {/* Predictive churn tier with honest confidence. */}
                        <span
                          className={TIER_PILL[explainRow.churn.tier]}
                          style={{ fontSize: 10 }}
                          title={
                            explainRow.churn.leadingIndicators.length > 0
                              ? `Leading indicators: ${explainRow.churn.leadingIndicators.join(', ')}`
                              : 'No named leading indicators active'
                          }
                        >
                          Churn {explainRow.churn.tier} ·{' '}
                          {Math.round(explainRow.churn.probability * 100)}% (conf{' '}
                          {Math.round(explainRow.churn.confidence * 100)}%)
                        </span>
                      </span>
                    )}
                    {/* Governed weekly update — preview the exact composed
                        email (or the honest empty state) and queue the same
                        send_customer_update proposal the agent would. */}
                    <CustomerWeeklyUpdatePanel
                      person={person}
                      deliverables={deliverables}
                      tickets={tickets}
                      tasks={tasks}
                      canQueue={canMutate}
                    />
                  </span>
                  </details>
                </li>
              );
            })}
          </ul>
        )}
      </GlassPanel>

      {/* Cadence engine (Ask #1) — recurring follow-up governance: accounts
          whose silence exceeds their per-tier touch cadence, with a governed
          "Propose check-ins" run that queues approvals (never auto-writes). */}
      <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
        <div className="dash-section-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              aria-hidden="true"
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                display: 'inline-grid',
                placeItems: 'center',
                background: overdueTouches.length
                  ? 'linear-gradient(135deg, rgba(255,140,0,0.22), rgba(255,0,255,0.10))'
                  : 'linear-gradient(135deg, rgba(0,200,120,0.20), rgba(0,130,243,0.10))',
                border: '1px solid rgba(255,255,255,0.14)',
                color: overdueTouches.length ? C.orange : C.green,
              }}
            >
              <CalendarClock size={17} strokeWidth={2} />
            </span>
            <div>
              <span className="t-eyebrow">Ask #1</span>
              <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
                Touch cadence
              </h2>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {canMutate && overdueTouches.length > 0 && (
              <button
                className="glass-btn glass-btn--primary"
                onClick={() => void onProposeCheckins()}
                disabled={cadenceRunning}
                title="Queue a governed check-in proposal for every overdue account into the Dashboard approvals inbox. Approve there to create the task — nothing writes without you."
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                <Bot size={13} /> {cadenceRunning ? 'Proposing…' : 'Propose check-ins'}
              </button>
            )}
            {overdueTouches.length > 0 && (
              <span className="glass-pill glass-pill--orange">
                {overdueTouches.length} overdue
              </span>
            )}
          </div>
        </div>

        <div
          className="t-meta"
          style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 10px' }}
        >
          <Info size={12} aria-hidden="true" />
          <span>
            Every account gets a touch rhythm from its real record — enterprise every 14d, mid
            every 21d, standard every 30d (paused accounts relaxed). Last touch comes from the
            email trail and the contact timeline; nothing is invented.
          </span>
        </div>

        {cadenceResult && (
          <div
            className="t-meta"
            style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 10px' }}
          >
            <Activity size={12} color={C.magentaSoft} /> {cadenceResult}
          </div>
        )}

        {overdueTouches.length === 0 ? (
          <div className="crm-empty" style={{ padding: '20px 0' }}>
            <ShieldCheck size={20} strokeWidth={1.75} />
            <span>
              All current — every account has been touched within its cadence. Nothing to chase.
            </span>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: '4px 0 0', padding: 0, display: 'grid', gap: 8 }}>
            {overdueTouches.map((due) => (
              <li
                key={due.account.id}
                className="glass-row"
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px' }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    className="t-card-title"
                    style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {due.account.name}
                  </span>
                  <span className="t-meta" style={{ display: 'block', marginTop: 2 }}>
                    {due.reason}
                  </span>
                </span>
                {due.arr != null && (
                  <span
                    className="t-meta"
                    style={{ flex: '0 0 auto', fontVariantNumeric: 'tabular-nums' }}
                    title="Annual account value at stake"
                  >
                    {usd(due.arr)} ARR
                  </span>
                )}
                <span className="glass-pill" style={{ flex: '0 0 auto' }} title="Cadence tier from ARR">
                  {due.tier}
                </span>
                <span
                  className={`glass-pill ${due.overdueDays >= 14 ? 'glass-pill--magenta' : 'glass-pill--orange'}`}
                  style={{ flex: '0 0 auto', fontVariantNumeric: 'tabular-nums' }}
                >
                  {overdueLabel(due)}
                </span>
                <span className="glass-pill glass-pill--blue" style={{ flex: '0 0 auto' }}>
                  {due.suggestedAction === 'qbr' ? 'Schedule QBR' : 'Check in'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </GlassPanel>

      {/* Deliverables — first-class per-account objects (Ask #1): what each
          customer is owed, its status, and whether we're late on it. */}
      <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
        <div className="dash-section-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              aria-hidden="true"
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                display: 'inline-grid',
                placeItems: 'center',
                background: 'linear-gradient(135deg, rgba(255,140,0,0.20), rgba(0,130,243,0.10))',
                border: '1px solid rgba(255,255,255,0.14)',
                color: C.orange,
              }}
            >
              <ClipboardList size={17} strokeWidth={2} />
            </span>
            <div>
              <span className="t-eyebrow">Ask #1</span>
              <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
                Deliverables
              </h2>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {deliverablesSummary.overdue.length > 0 && (
              <span className="glass-pill glass-pill--magenta">
                {deliverablesSummary.overdue.length} overdue
              </span>
            )}
            {deliverablesSummary.byStatus.owed > 0 && (
              <span className="glass-pill glass-pill--orange">
                {deliverablesSummary.byStatus.owed} owed
              </span>
            )}
            {deliverablesSummary.byStatus['in-progress'] > 0 && (
              <span className="glass-pill glass-pill--blue">
                {deliverablesSummary.byStatus['in-progress']} in progress
              </span>
            )}
          </div>
        </div>

        <div
          className="t-meta"
          style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 10px' }}
        >
          <Info size={12} aria-hidden="true" />
          <span>
            What each customer is owed, tracked per account — owed → in progress → delivered →
            accepted. Overdue means a committed date passed before delivery.
          </span>
        </div>

        {sortedAccounts.length === 0 ? (
          <div className="crm-empty" style={{ padding: '20px 0' }}>
            <ClipboardList size={20} strokeWidth={1.75} />
            <span>No accounts yet — add one in the Accounts view to track its deliverables.</span>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {sortedAccounts.map((account) => (
              <AccountDeliverables
                key={account.id}
                account={account}
                items={deliverablesByAccount.get(account.id) ?? []}
                canMutate={canMutate}
              />
            ))}
          </div>
        )}
      </GlassPanel>

      {/* Weekly Digest — worker-drafted, persona-tailored, governed send/recall
          with engagement tracking. All logic lives in pure exports
          (digestComposer / digestSendPipeline / digestEngagement); the ONLY
          send path is digestSendPipeline.approveAndSend. */}
      <WeeklyDigestPanel
        accounts={sortedAccounts}
        tasks={tasks}
        tickets={tickets}
        deliverables={deliverables}
        roadmap={roadmap}
        canSend={canMutate}
      />

      {/* Weekly customer updates — governed draft → approve → send, sealed
          in the Decision Ledger. Drafting assembles the digest from the live
          boards at click time; the modal is the human approval gate. */}
      <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
        <div className="dash-section-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              aria-hidden="true"
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                display: 'inline-grid',
                placeItems: 'center',
                background: 'linear-gradient(135deg, rgba(0,130,243,0.20), rgba(47,1,253,0.10))',
                border: '1px solid rgba(255,255,255,0.14)',
                color: C.blueElectric,
              }}
            >
              <Mail size={17} strokeWidth={2} />
            </span>
            <div>
              <span className="t-eyebrow">Ask #2</span>
              <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
                Weekly customer updates
              </h2>
            </div>
          </div>
          {canMutate && sortedAccounts.length > 0 && (
            <button
              className="glass-btn glass-btn--primary"
              onClick={onDraftAllWeeklyUpdates}
              title="Assemble this week's digest for EVERY account and review the whole batch before anything sends"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
            >
              <Mail size={13} /> Send to all accounts…
            </button>
          )}
        </div>

        <div
          className="t-meta"
          style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 10px' }}
        >
          <Info size={12} aria-hidden="true" />
          <span>
            Drafts an honest digest of the trailing week from the live boards — you review, edit,
            and approve before anything sends. Every send is sealed in the Decision Ledger.
          </span>
        </div>

        {sortedAccounts.length === 0 ? (
          <div className="crm-empty" style={{ padding: '20px 0' }}>
            <ShieldCheck size={20} strokeWidth={1.75} />
            <span>No accounts yet — add one in the Accounts view to draft its weekly update.</span>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: '4px 0 0', padding: 0, display: 'grid', gap: 8 }}>
            {sortedAccounts.map((account) => {
              const recipient = resolveWeeklyUpdateRecipient(account, people);
              return (
                <li
                  key={account.id}
                  className="glass-row"
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px' }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      className="t-card-title"
                      style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {account.name}
                    </span>
                    <span className="t-meta" style={{ display: 'block', marginTop: 2 }}>
                      {recipient
                        ? `Sends to ${recipient.name} <${recipient.email}>`
                        : 'No contact email on file — send will be disabled'}
                    </span>
                  </span>
                  {canMutate && (
                    <button
                      className="glass-btn"
                      onClick={() => onDraftWeeklyUpdate(account)}
                      title="Assemble this week's digest from the live boards and review it before sending"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flex: '0 0 auto' }}
                    >
                      <Mail size={13} /> Draft weekly update
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </GlassPanel>

      </FullAnalysisFold>

      {draft && (
        <WeeklyUpdateReviewModal
          account={draft.account}
          digest={draft.digest}
          recipient={draft.recipient}
          onClose={() => setDraft(null)}
        />
      )}

      {batch && <BatchWeeklyUpdateModal entries={batch} onClose={() => setBatch(null)} />}
    </div>
  );
}

/** Format 'YYYY-MM-DD' → 'Jun 15' for the due-date chip. */
function shortDate(iso: string): string {
  const ms = Date.parse(`${iso}T12:00:00Z`);
  if (!Number.isFinite(ms)) return iso;
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * One account's deliverables: status rollup, the list with inline status
 * change + overdue flags, and the add form. Thin by design — the rollup
 * math lives in `crm/deliverablesSummary.ts` and writes go through
 * `db/deliverables.ts`.
 */
function AccountDeliverables({
  account,
  items,
  canMutate,
}: {
  account: Account;
  items: Deliverable[];
  canMutate: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [owner, setOwner] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [status, setStatus] = useState<Deliverable['status']>('owed');

  const nowMs = Date.now();
  const summary = useMemo(() => summarizeDeliverables(items, nowMs), [items, nowMs]);
  // Committed dates first (soonest up top), undated last, then by name.
  const sorted = useMemo(
    () =>
      [...items].sort(
        (a, b) =>
          (a.dueDate ?? '9999-12-31').localeCompare(b.dueDate ?? '9999-12-31') ||
          a.name.localeCompare(b.name),
      ),
    [items],
  );

  async function onAdd() {
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      await saveDeliverable({
        accountId: account.id,
        accountName: account.name,
        name: trimmed,
        owner: owner.trim() || undefined,
        dueDate: dueDate || null,
        status,
      });
      setName('');
      setOwner('');
      setDueDate('');
      setStatus('owed');
      setAdding(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="glass-row" style={{ padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="t-card-title" style={{ flex: 1, minWidth: 0 }}>
          {account.name}
        </span>
        {summary.overdue.length > 0 && (
          <span className="glass-pill glass-pill--magenta" style={{ fontSize: 10 }}>
            <AlertTriangle size={10} style={{ marginRight: 3 }} />
            {summary.overdue.length} overdue
          </span>
        )}
        <span className="t-meta" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {summary.total === 0
            ? 'Nothing tracked'
            : `${summary.byStatus.delivered + summary.byStatus.accepted}/${summary.total} delivered`}
        </span>
        {canMutate && !adding && (
          <button
            className="glass-btn"
            onClick={() => setAdding(true)}
            title="Track a new deliverable this account is owed"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            <Plus size={13} /> Add deliverable
          </button>
        )}
      </div>

      {sorted.length === 0 && !adding ? (
        <p className="t-meta" style={{ margin: '8px 0 0' }}>
          No deliverables tracked yet — nothing owed on record for this account.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'grid', gap: 6 }}>
          {sorted.map((d) => {
            const meta = DELIVERABLE_STATUS_META[d.status];
            const overdue = isDeliverableOverdue(d, nowMs);
            return (
              <li
                key={d.id}
                style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
              >
                <span style={{ flex: 1, minWidth: 160 }}>
                  <span
                    className="t-card-title"
                    style={{ display: 'block', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {d.name}
                  </span>
                  <span className="t-meta" style={{ display: 'block', marginTop: 1 }}>
                    {[
                      d.owner ? `Owner: ${d.owner}` : null,
                      d.dueDate ? `Due ${shortDate(d.dueDate)}` : 'No committed date',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                {overdue && (
                  <span
                    className="glass-pill glass-pill--magenta"
                    style={{ fontSize: 10, flex: '0 0 auto' }}
                    title="Committed date passed before delivery"
                  >
                    <AlertTriangle size={10} style={{ marginRight: 3 }} /> Overdue
                  </span>
                )}
                {canMutate ? (
                  <select
                    className="glass-input"
                    value={d.status}
                    onChange={(e) =>
                      void updateDeliverable(d.id, {
                        status: e.target.value as Deliverable['status'],
                      })
                    }
                    title="Change status"
                    style={{ flex: '0 0 auto', width: 130, fontFamily: 'Manrope, sans-serif' }}
                  >
                    {DELIVERABLE_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {DELIVERABLE_STATUS_META[s].label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className={meta.pill} style={{ flex: '0 0 auto', fontSize: 10 }}>
                    {meta.label}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {canMutate && adding && (
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 10 }}
        >
          <input
            className="glass-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="What is this account owed?"
            style={{ flex: '1 1 180px' }}
          />
          <input
            className="glass-input"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="Owner"
            style={{ flex: '0 1 110px' }}
          />
          <input
            className="glass-input"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            title="Committed due date (optional)"
            style={{ flex: '0 0 auto' }}
          />
          <select
            className="glass-input"
            value={status}
            onChange={(e) => setStatus(e.target.value as Deliverable['status'])}
            style={{ flex: '0 0 auto', fontFamily: 'Manrope, sans-serif' }}
          >
            {DELIVERABLE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {DELIVERABLE_STATUS_META[s].label}
              </option>
            ))}
          </select>
          <button
            className="glass-btn glass-btn--primary"
            onClick={() => void onAdd()}
            disabled={!name.trim() || saving}
          >
            {saving ? 'Adding…' : 'Add'}
          </button>
          <button className="glass-btn" onClick={() => setAdding(false)} disabled={saving}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
